import React from "react";
import Layout from "layout";
import TransactionStats from "components/transactionStats";
import { TransactionTable } from "./components";
import { useTransactionManagement } from "./hooks/useTransactionManagement";
import { TRANSACTION_TYPE } from "utils/types/user.type";

export default function TransactionHistory() {
  const {
    activeTab,
    setActiveTab,
    searchValue,
    setSearchValue,
    statusFilter,
    setStatusFilter,
    transactions,
    isLoading,
    tabCounts,
  } = useTransactionManagement();

  const tabs = [
    { id: TRANSACTION_TYPE.WITHDRAW, label: "Withdraw" },
    { id: TRANSACTION_TYPE.DEPOSIT, label: "Deposit" },
    { id: "buy_sell", label: "Buy/Sell" },
    { id: TRANSACTION_TYPE.SWAP, label: "Swap" },
  ];

  const statusOptions = [
    { value: "completed", label: "Completed", color: "bg-green-500" },
    { value: "pending", label: "Pending", color: "bg-yellow-500" },
    { value: "rejected", label: "Rejected", color: "bg-red-500" },
    { value: "in-progress", label: "In Progress", color: "bg-blue-500" },
  ];

  return (
    <Layout>
      <div className="dashboard-main">
        <div className="main-wrapper max-w-7xl p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-black-900 mb-2">
              Transaction Management
            </h1>
            <p className="text-black-700 text-sm">
              Monitor and manage all transactions across your platform
            </p>
          </div>

          {/* Transaction Stats */}
          <TransactionStats activeTab={activeTab} />

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-6">
              {/* Search Bar */}
              <div className="flex-1 w-full lg:max-w-2xl">
                <div className="relative">
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search by email, transaction type, coin, or date..."
                    className="w-full text-black-700 text-sm rounded-lg border border-black-800/30 focus:outline-0 focus:border-cyan-800 focus:ring-2 focus:ring-cyan-300 py-3.5 px-5 transition"
                  />
                  {searchValue && (
                    <button
                      onClick={() => setSearchValue("")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-black-700 hover:text-black-900"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex gap-2">
                {statusOptions.map((status) => (
                  <button
                    key={status.value}
                    onClick={() => setStatusFilter(status.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      statusFilter === status.value
                        ? `${status.color} text-white`
                        : "bg-gray-200 text-black-700 hover:bg-gray-300"
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const count = tabCounts[tab.id] || 0;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition ${
                      isActive
                        ? "bg-cyan-800 text-white shadow-md"
                        : "bg-gray-100 text-black-700 hover:bg-gray-200"
                    }`}
                  >
                    <span>{tab.label}</span>
                    {count > 0 && (
                      <span
                        className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                          isActive ? "bg-white text-cyan-800" : "bg-cyan-300 text-black-800"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-black-700 text-sm">
              Showing{" "}
              <span className="font-bold text-black-900">
                {transactions?.length || 0}
              </span>{" "}
              {activeTab === "buy_sell"
                ? "buy/sell transactions"
                : `${activeTab.toLowerCase()} transactions`}
              {searchValue && (
                <span className="ml-1">
                  matching &quot;<span className="font-semibold">{searchValue}</span>&quot;
                </span>
              )}
            </p>
            
            {transactions && transactions.length > 0 && (
              <button
                onClick={() => {
                  // Export functionality can be added here
                  alert("Export feature coming soon!");
                }}
                className="px-4 py-2 bg-white border border-black-800/30 rounded-lg text-sm font-semibold text-black-800 hover:bg-gray-50 transition"
              >
                Export
              </button>
            )}
          </div>

          {/* Transaction Table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <TransactionTable transactions={transactions} isLoading={isLoading} />
          </div>

          {/* Pagination Placeholder */}
          {transactions && transactions.length > 0 && (
            <div className="flex items-center justify-center mt-6 gap-2">
              <button className="px-4 py-2 bg-white border border-black-800/30 rounded-lg text-sm font-semibold text-black-800 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                ← Previous
              </button>
              <div className="flex gap-1">
                {[1, 2, 3].map((page) => (
                  <button
                    key={page}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      page === 1
                        ? "bg-cyan-800 text-white"
                        : "bg-white border border-black-800/30 text-black-800 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button className="px-4 py-2 bg-white border border-black-800/30 rounded-lg text-sm font-semibold text-black-800 hover:bg-gray-50 transition">
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

